import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { MetricsTransactionDataModel } from '../../../schemas/MetricsTransaction.schema';
import { fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { getIsoDateColDef } from '../../../util/ag-grid-utils';
import { PpsFormatterDataModel } from '../../../util/formatters/NumericFormatters';
import { ColoredNumericRenderer } from '../common-grid-defs/ColoredNumericRenderer';
import { genericKeyCreator, getCurrencyColDefs, getIntegerColDefs } from '../common-grid-defs/commonColDefs';
import { fundLabelForTransaction } from '../fundLabel';
import { eventFormatter } from './securityUtils';

export function useLedgerColDefs(): ColDef<MetricsTransactionDataModel>[] {
  const fundsById = useAtomValue(fundsByIdMapAtom);

  const defs: ColDef<MetricsTransactionDataModel>[] = [
    {
      headerName: 'Security',
      field: 'position',
      rowGroup: true,
      enableRowGroup: true,
      allowedAggFuncs: [],
      minWidth: 200,
      keyCreator: genericKeyCreator,
    },
    getIsoDateColDef({
      headerName: 'Event Date',
      field: 'transactionDate',
      initialSort: 'desc',
      keyCreator: genericKeyCreator,
    }),
    {
      headerName: 'Event',
      field: 'transType',
      valueFormatter: (params) => {
        return eventFormatter(params.value ?? '');
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      enableRowGroup: true,
      keyCreator: genericKeyCreator,
    },
    {
      headerName: 'Fund',
      valueGetter: (params: ValueGetterParams<MetricsTransactionDataModel>) => {
        return fundLabelForTransaction(params.data, fundsById);
      },
      enableRowGroup: true,
      keyCreator: genericKeyCreator,
      minWidth: 200,
    },
    {
      headerName: 'Investment Amount',
      field: 'investmentAmount',
      aggFunc: 'sum',
      ...getCurrencyColDefs(),
      cellRenderer: ColoredNumericRenderer,
    },
    {
      headerName: 'FMV',
      field: 'currentInvestment',
      aggFunc: 'sum',
      ...getCurrencyColDefs(),
      cellRenderer: ColoredNumericRenderer,
    },
    {
      headerName: 'Proceeds',
      field: 'distributions',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Contribution to Escrow',
      field: 'escrowAmount',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Total Cost',
      field: 'totalCost',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Return of Capital',
      field: 'rocAmount',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Capitalized Expenses',
      field: 'expenses',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Capitalized Interest',
      field: 'capInterest',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Restructure Amount',
      field: 'restructureAmount',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Stock Proceeds',
      field: 'stockProceeds',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Recallable Amount',
      field: 'recallableAmount',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Dividends',
      field: 'dividends',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Cash Interest Received',
      field: 'cashInterestReceived',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Cash Dividend Received',
      field: 'cashDividendReceived',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getCurrencyColDefs(),
    },
    {
      headerName: 'Number of Shares',
      field: 'quantity',
      aggFunc: 'sum',
      cellRenderer: ColoredNumericRenderer,
      ...getIntegerColDefs(),
    },
    {
      headerName: 'PPS',
      field: 'pps',
      colId: 'pps',
      ...getCurrencyColDefs(),
      valueFormatter: AgFormatterService.get().getFormatterForModel(PpsFormatterDataModel),
    } as ColDef<MetricsTransactionDataModel>,
    getIsoDateColDef({
      headerName: 'Date Modified',
    }),
  ];

  return defs;
}
